import { useEffect, useMemo, useState } from "react";
import { InfoAlert } from "./styles";
import headerImg from "./imgs/your-stay-banner.webp";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ClaimGroupModal = ({
  groups = [],
  initialValues,
  loadingGroups,
  loadingGroupsError,
  submitting,
  submitError,
  onSubmit,
  onTrackButtonClick
}) => {
  const [step, setStep] = useState("form");
  const [formValues, setFormValues] = useState({
    guestFirstName: "",
    guestLastName: "",
    email: "",
    guestGroupId: ""
  });
  const [hasSubmittedForm, setHasSubmittedForm] = useState(false);

  useEffect(() => {
    setFormValues({
      guestFirstName: initialValues?.guestFirstName || "",
      guestLastName: initialValues?.guestLastName || "",
      email: initialValues?.email || "",
      guestGroupId: ""
    });
  }, [initialValues]);

  const normalizedGroups = useMemo(() => {
    return (groups || [])
      .map((group) => {
        if (typeof group === "string") {
          return { id: group, name: group };
        }

        const hotelName = group?.customFields?.hotelName?.value;

        return {
          id:
            group?.id ||
            group?.groupId ||
            group?.guestGroupId ||
            group?.value ||
            "",
          name: hotelName || ""
        };
      })
      .filter((group) => group.id && group.name);
  }, [groups]);

  const errors = useMemo(() => {
    const nextErrors = {};

    if (!formValues.guestFirstName.trim()) {
      nextErrors.guestFirstName = "First name is required.";
    }
    if (!formValues.guestLastName.trim()) {
      nextErrors.guestLastName = "Last name is required.";
    }
    if (!formValues.email.trim()) {
      nextErrors.email = "Email is required.";
    } else if (!EMAIL_REGEX.test(formValues.email.trim())) {
      nextErrors.email = "Enter a valid email address.";
    }
    if (!formValues.guestGroupId) {
      nextErrors.guestGroupId = "Please select your group.";
    }

    return nextErrors;
  }, [formValues]);

  const isValid = Object.keys(errors).length === 0;
  const selectedGroup = normalizedGroups.find(
    (group) => group.id === formValues.guestGroupId
  );

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleContinue = () => {
    setHasSubmittedForm(true);
    if (!isValid) {
      return;
    }

    onTrackButtonClick?.("continue_to_confirmation", "claim_group_modal");
    setStep("confirm");
  };

  const handleConfirmSubmit = () => {
    onTrackButtonClick?.("confirm_claim_submit", "claim_group_modal");
    onSubmit({
      guestFirstName: formValues.guestFirstName.trim(),
      guestLastName: formValues.guestLastName.trim(),
      email: formValues.email.trim(),
      guestGroupId: formValues.guestGroupId
    });
  };

  return (
    <InfoAlert>
      <div className="inner-container">
        <div className="img-container">
          <img src={headerImg} alt="header" />
          <h2>{"Your Details"}</h2>
        </div>
        <div className="scroll-view">
          {step === "form" && (
            <div className="section">
              <p>
                {
                  "Please complete your details to continue. This is needed only once."
                }
              </p>
            </div>
          )}
          {step === "form" && (
            <div className="section claim-form">
              <label htmlFor="guestFirstName">First Name</label>
              <input
                id="guestFirstName"
                name="guestFirstName"
                value={formValues.guestFirstName}
                onChange={handleInputChange}
                disabled={submitting}
              />
              {hasSubmittedForm && errors.guestFirstName && (
                <p className="error-message">{errors.guestFirstName}</p>
              )}

              <label htmlFor="guestLastName">Last Name</label>
              <input
                id="guestLastName"
                name="guestLastName"
                value={formValues.guestLastName}
                onChange={handleInputChange}
                disabled={submitting}
              />
              {hasSubmittedForm && errors.guestLastName && (
                <p className="error-message">{errors.guestLastName}</p>
              )}

              <label htmlFor="email">Email</label>
              <input
                id="email"
                name="email"
                value={formValues.email}
                onChange={handleInputChange}
                disabled={submitting}
                type="email"
              />
              {hasSubmittedForm && errors.email && (
                <p className="error-message">{errors.email}</p>
              )}

              <label htmlFor="guestGroupId">Hotel</label>
              <select
                id="guestGroupId"
                name="guestGroupId"
                value={formValues.guestGroupId}
                onChange={handleInputChange}
                disabled={loadingGroups || submitting}
              >
                <option value="">Select your hotel</option>
                {normalizedGroups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ))}
              </select>
              {hasSubmittedForm && errors.guestGroupId && (
                <p className="error-message">{errors.guestGroupId}</p>
              )}
              {loadingGroups && <p>{"Loading groups..."}</p>}
              {loadingGroupsError && (
                <p className="error-message">{loadingGroupsError}</p>
              )}

              <div
                className={`button ${submitting ? "button-disabled" : ""}`}
                onClick={submitting ? undefined : handleContinue}
              >
                <p>{"Continue"}</p>
              </div>
            </div>
          )}

          {step === "confirm" && (
            <div className="section claim-form">
              <p className="bold">{"Please confirm your details"}</p>
              <div className="claim-confirm-item">
                <p className="bold">{"First Name"}</p>
                <p>{formValues.guestFirstName}</p>
              </div>
              <div className="claim-confirm-item">
                <p className="bold">{"Last Name"}</p>
                <p>{formValues.guestLastName}</p>
              </div>
              <div className="claim-confirm-item">
                <p className="bold">{"Email"}</p>
                <p>{formValues.email}</p>
              </div>
              <div className="claim-confirm-item">
                <p className="bold">{"Group"}</p>
                <p>{selectedGroup?.name || formValues.guestGroupId}</p>
              </div>
              {submitError && <p className="error-message">{submitError}</p>}
              <div
                className={`button button-black ${submitting ? "button-disabled" : ""}`}
                style={{ marginTop: "16px" }}
                onClick={() => {
                  if (submitting) return;
                  onTrackButtonClick?.("edit_claim_data", "claim_group_modal");
                  setStep("form");
                }}
              >
                <p>{"Edit"}</p>
              </div>
              <div
                className={`button ${submitting ? "button-disabled" : ""}`}
                onClick={submitting ? undefined : handleConfirmSubmit}
              >
                <p>{submitting ? "Submitting..." : "Confirm & Submit"}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </InfoAlert>
  );
};

export default ClaimGroupModal;
